import { KernKopfzeile } from '@kern-ux-annex/kern-react-kit'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useId, useRef, useState } from 'react'
import { NavLink, ScrollRestoration, useLocation, useOutlet } from 'react-router'

import { useTenant } from '../api/hooks'
import { auth } from '../auth/auth'
import { APP_VERSION, DevPanel, useSecretTaps } from '../components/DevPanel'
import { Loading } from '../components/feedback'
import { ThemeSwitch } from '../components/layout'
import { areaOf, PLATFORM_HOME, TENANT_HOME } from './area'
import { PlatformContext, PlatformNav, TenantContextView } from './HeaderContext'
import { ProcessNav } from './ProcessNav'
import { hideSplash } from './splash'
import { TenantProvider, useTenantState } from './tenant'

/**
 * Account menu: what is needed rarely (settings, appearance, sign out) behind the user's name,
 * so the header keeps one task per area. Disclosure pattern; closes on Escape and outside click.
 */
function UserMenu({ name, showSettings, platformAdmin }: { name: string; showSettings: boolean; platformAdmin: boolean }) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { pathname } = useLocation()
  const session = auth()

  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="dms-focus flex items-center gap-8 rounded-kern-default px-8 py-4 text-kern-16"
        style={{ color: 'var(--dms-text)' }}
      >
        <span
          className="inline-flex size-32 items-center justify-center rounded-full text-kern-16 font-semibold"
          style={{ background: 'var(--dms-surface-raised)', border: '1px solid var(--dms-border)' }}
          aria-hidden="true"
        >
          {initials(name)}
        </span>
        <span className="flex flex-col text-left leading-tight">
          <span>{name}</span>
          {platformAdmin && <span className="dms-caption">Plattform-Administration</span>}
        </span>
        <span className={`kern-icon kern-icon--arrow-down transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            id={panelId}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="dms-card absolute right-0 z-30 mt-8 flex w-[280px] flex-col gap-16 p-16 shadow-lg"
          >
            {(showSettings || platformAdmin) && (
              // menu entries, not links in running text: no underline, a hover area instead
              <ul className="-mx-8 flex flex-col gap-2">
                {showSettings && (
                  <li>
                    <NavLink to="/einstellungen" className="dms-menu-item dms-focus">
                      Einstellungen der Kommune
                    </NavLink>
                  </li>
                )}
                {showSettings && (
                  <li>
                    <NavLink to="/aenderungen" className="dms-menu-item dms-focus">
                      Änderungsprotokoll
                    </NavLink>
                  </li>
                )}
                {platformAdmin && (
                  <li>
                    <NavLink to={PLATFORM_HOME} end className="dms-menu-item dms-focus">
                      Alle Kommunen
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
            <ThemeSwitch stacked />
            {session.enabled && (
              <button
                type="button"
                className="kern-btn kern-btn--secondary kern-btn--block"
                onClick={session.logout}
              >
                <span className="kern-label">Abmelden</span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/** "Pascal Giessler" → "PG"; text in brackets and non-letters are ignored. */
const initials = (name: string) =>
  name
    .replace(/\(.*?\)/g, '')
    .split(/\s+/)
    .map((part) => part.match(/\p{L}/u)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

function Header() {
  const { me, current, select } = useTenantState()
  const { pathname } = useLocation()
  const platform = areaOf(pathname) === 'platform'
  const { data: tenant } = useTenant(Boolean(current) && !platform)
  return (
    <header className="no-print border-b" style={{ borderColor: 'var(--dms-border)' }}>
      <KernKopfzeile label="Fachanwendung" fluid />
      <div className="mx-auto flex max-w-[1600px] flex-col gap-12 px-24 pt-16 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-16">
          <div className="flex flex-wrap items-center gap-x-32 gap-y-12">
            {/* the logo leads to the start of the area one is in */}
            <NavLink to={platform ? PLATFORM_HOME : TENANT_HOME} end className="dms-focus flex items-center gap-12 no-underline" style={{ color: 'var(--dms-text)' }}>
              <img src="/favicon.svg" alt="" className="size-32" />
              <span className="flex flex-col">
                <span className="text-kern-20 font-semibold">Drittmittelscan</span>
                <span className="dms-muted text-kern-16">Drittmittelbasierte Investitionssteuerung</span>
              </span>
            </NavLink>
            {platform ? (
              <PlatformContext />
            ) : (
              <TenantContextView
                tenants={me.tenants}
                current={current}
                platformAdmin={me.platformAdmin}
                logoUrl={tenant?.logoUrl}
                onSelect={select}
              />
            )}
          </div>
          <UserMenu name={me.name} showSettings={Boolean(current) && !platform} platformAdmin={me.platformAdmin} />
        </div>
        {platform ? <PlatformNav /> : current && <ProcessNav />}
      </div>
    </header>
  )
}

function Footer() {
  const [panel, setPanel] = useState(false)
  const tap = useSecretTaps(() => setPanel(true))
  return (
    <footer className="no-print border-t px-24 py-16 dms-muted text-kern-16" style={{ borderColor: 'var(--dms-border)' }}>
      <div className="mx-auto flex max-w-[1600px] flex-wrap justify-between gap-8">
        <span>Drittmittelscan · Design-System KERN UX</span>
        {/* seven taps open the developer panel (feature toggles) */}
        <button type="button" onClick={tap} className="cursor-default select-none" aria-label={`Version ${APP_VERSION}`}>
          Version {APP_VERSION}
        </button>
      </div>
      <DevPanel open={panel} onClose={() => setPanel(false)} />
    </footer>
  )
}

function Page() {
  const location = useLocation()
  const outlet = useOutlet()
  // animate between pages, but not inside one: neither between the steps of a wizard nor when
  // its first save turns /massnahmen/neu into /massnahmen/<id> – a new key would remount the
  // wizard and drop what was typed during the exit animation
  const [, first = '', second] = location.pathname.split('/')
  const section = second ? `/${first}/:detail` : `/${first}`
  useEffect(() => hideSplash(), [])
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-8">
        Zum Inhalt springen
      </a>
      <Header />
      <main id="main" className="mx-auto w-full max-w-[1600px] flex-1 px-24 py-24">
        {/* The new page mounts at once and only fades in. An exit animation (AnimatePresence
            mode="wait") kept the old, often shorter page in the DOM while ScrollRestoration
            already restored the position – Back to a long list then ended up too high. */}
        <motion.div
          key={section}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex flex-col gap-24"
        >
          {outlet}
        </motion.div>
      </main>
      <Footer />
      {/* Scroll rule: new content (another page, a wizard step) starts at the top, Back restores the
          position, and changing the view of the same content (sort, filter – everything going
          through useUrlState) keeps it via preventScrollReset. */}
      <ScrollRestoration />
    </div>
  )
}

/** Municipality-scoped pages need a municipality; the platform works without one. */
export function Layout() {
  return (
    <TenantProvider
      loading={
        <div className="mx-auto max-w-[1600px] px-24 py-48">
          <Loading label="Anmeldung wird geprüft …" />
        </div>
      }
    >
      <Page />
    </TenantProvider>
  )
}
